import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";

const PRIORITIES = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];
const STATUSES = [["not_started", "Not Started"], ["in_progress", "In Process"], ["waiting", "Waiting"], ["completed", "Completed"]];

export function PersonalTaskDialog({ taskId, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [step, setStep] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    api.get(`/tasks/${taskId}`).then((r) => { setT(r.data.task); setComments(r.data.comments || []); }).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects((r.data.items || []).filter((p) => !p.system_managed))).catch(() => {}); }, [load]);
  const save = async (patch) => {
    try { await api.patch(`/tasks/${taskId}`, patch); load(); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const addStep = async () => {
    if (!step.trim()) return;
    try { await api.post(`/tasks/${taskId}/checklist`, { text: step.trim() }); setStep(""); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleStep = async (c) => { try { await api.patch(`/tasks/${taskId}/checklist/${c.id}`, { done: !c.done }); load(); } catch (e) { toast.error(apiError(e)); } };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/tasks/${taskId}/comments`, { body: note.trim() }); setNote(""); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/tasks/${taskId}`); toast.success("Task deleted"); onChanged(); onClose(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="personal-task-dialog">
        <DialogHeader><DialogTitle>My Task</DialogTitle><DialogDescription>Your own task — it doesn't change your program progress.</DialogDescription></DialogHeader>
        {!t ? <p className="text-sm text-slate-400">Loading…</p> : (
          <div className="space-y-4">
            <Input defaultValue={t.title} onBlur={(e) => e.target.value.trim() && e.target.value !== t.title && save({ title: e.target.value.trim() })} data-testid="personal-task-title" />
            <Textarea defaultValue={t.description || ""} rows={3} placeholder="Description" onBlur={(e) => e.target.value !== (t.description || "") && save({ description: e.target.value })} data-testid="personal-task-desc" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={t.status} onValueChange={(v) => save({ status: v })}>
                <SelectTrigger data-testid="personal-task-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={t.importance === "critical" ? "urgent" : t.importance || "normal"} onValueChange={(v) => save({ priority: v })}>
                <SelectTrigger data-testid="personal-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" defaultValue={(t.due_date || "").slice(0, 10)} onChange={(e) => save({ due_date: e.target.value || null })} data-testid="personal-task-due" />
              <Select value={t.project_id || "none"} onValueChange={(v) => save({ project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="personal-task-project"><SelectValue placeholder="Project" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Checklist</p>
              <div className="space-y-1.5">{(t.checklist || []).map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`personal-step-${c.id}`}>
                  <Checkbox checked={!!c.done} onCheckedChange={() => toggleStep(c)} /><span className={c.done ? "line-through text-slate-400" : "text-slate-700"}>{c.text}</span>
                </label>))}</div>
              <div className="flex gap-2 mt-2">
                <Input value={step} onChange={(e) => setStep(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStep()} placeholder="Add a step" data-testid="personal-step-input" />
                <Button size="sm" variant="outline" onClick={addStep} data-testid="personal-step-add"><Plus className="w-4 h-4" /></Button>
              </div>
            </section>
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Notes</p>
              <div className="space-y-1.5">{comments.map((c) => <p key={c.id} className="text-sm text-slate-700 bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid="personal-note">{c.body}</p>)}</div>
              <div className="flex gap-2 mt-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note" data-testid="personal-note-input" />
                <Button size="sm" variant="outline" onClick={addNote} data-testid="personal-note-add"><Send className="w-4 h-4" /></Button>
              </div>
            </section>
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={remove} className="text-rose-600" data-testid="personal-task-delete"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
          <Button onClick={onClose} className="bg-[#006090] hover:bg-[#004E75]" data-testid="personal-task-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
